import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { getTimetableRun, listMySchools, listClasses, listSubjects, listStaff, getPeriodTimes, getVocationalSchedule, getVanhoaDistribution, getStaffBusyWeek, getPeriodNotes, getMyStaff, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import { periodShadeIndex } from "./PeriodShadeStyle";
import { busyKeyToPairs } from "./HappySchoolAllTeachersView";
import HappySchoolCellSubstituteDialog from "./HappySchoolCellSubstituteDialog";
import HappySchoolCellFillDialog from "./HappySchoolCellFillDialog";
import HappySchoolCellSwapDialog from "./HappySchoolCellSwapDialog";
import { useRoomAssignment, RoomRow } from "./roomAssignment";

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Xuất Excel lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Tên sheet Excel: tối đa 31 ký tự, không được chứa : \ / ? * [ ]
function safeSheetName(name) {
  return String(name).replace(/[:\\/?*[\]]/g, "-").slice(0, 31);
}

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
// "Tổng ... tiết văn hoá/tuần" chỉ đếm tiết học văn hoá THẬT — loại các môn
// "ảo" (Nghỉ đã loại sẵn ở cellsByClass qua check "HN", Chào cờ/Sinh hoạt là
// môn thật có subjectId nên lọt qua check đó, phải loại riêng ở đây).
const EXCLUDED_FROM_VANHOA_TOTAL = new Set(["Nghỉ", "Thi KS", "HĐTN(CC)", "HĐTN(SH)", "HĐTN"]);
// Đủ chuẩn mỗi lớp/tuần: đúng 1 tiết mỗi loại HĐTN, HĐTN(SH), HĐTN(CC) = 3 tiết.
const HDTN_CATEGORY = new Set(["HĐTN", "HĐTN(SH)", "HĐTN(CC)"]);

function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const periods = (s.periods || []).map((time, i) => ({ global: cur + i, idx: i, time }));
    cur += (s.periods || []).length;
    return { key: s.key, label: s.label, periods };
  });
}
// Tách chuỗi "Ngày học" của nghề -> danh sách {d, s} (s: 0=sáng,1=chiều).
function ngheSlots(dayStr) {
  const str = String(dayStr).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const out = [];
  const re = /(sang|chieu|ngay)\s+thu\s+(\d)/g;
  let m;
  while ((m = re.exec(str))) {
    const dow = Number(m[2]);
    if (dow < 2 || dow > 7) continue;
    const d = dow - 2;
    (m[1] === "sang" ? [0] : m[1] === "chieu" ? [1] : [0, 1]).forEach((s) => out.push({ d, s }));
  }
  return out;
}

// "TKB cho GV & Học sinh — Toàn trường": TKB của TỪNG LỚP xếp liên tiếp trên
// 1 trang dài (kèm tên GV, giống hệt nội dung "TKB cho GV & Học sinh" đơn
// lẻ), thay vì phải chọn từng lớp qua combobox.
// studentMode (21/10/2026): node "TKB Học sinh — Toàn trường" — tiết Nghỉ và học nghề để TRỐNG, không sửa ô, có nút In.
const HappySchoolAllClassesView = ({ runId, weekId, canEdit: canEditProp = false, studentMode = false }) => {
  const canEdit = canEditProp && !studentMode;
  const schoolId = getActiveSchoolId();
  // (24/10/2026) Đổi tiết: GV bấm vào tiết CỦA MÌNH (ô viền xanh) -> form đổi tiết trong đúng lớp đó.
  const [myStaffId, setMyStaffId] = useState("");
  const [swapTarget, setSwapTarget] = useState(null); // {classId, day, period}
  const [onlyMine, setOnlyMine] = useState(false);
  const [swapMode, setSwapMode] = useState(false); // admin/BGH: bật "Chế độ đổi tiết" -> bấm 1 lần vào tiết BẤT KỲ để đổi (tắt nhấn đúp)
  const swapEnabled = !studentMode && !!myStaffId;
  useEffect(() => {
    if (!schoolId || studentMode) return;
    getMyStaff(schoolId).then((me) => setMyStaffId((me && me.staffId) || "")).catch(() => setMyStaffId(""));
  }, [schoolId, studentMode]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherName, setTeacherName] = useState({});
  const [sessions, setSessions] = useState([]);
  const [voc, setVoc] = useState({ rows: [] });
  const [vhDist, setVhDist] = useState({ rows: [] });
  const [teachers, setTeachers] = useState([]); // hồ sơ GV đầy đủ (cho dialog xử lý ô)
  const [allStaff, setAllStaff] = useState([]); // (24/10/2026) toàn bộ cán bộ — để GVCN thuộc khối khác (Phòng đào tạo...) vẫn chọn được ở dialog
  const [busyWeek, setBusyWeek] = useState({});
  const [periodNotes, setPeriodNotes] = useState({});
  const [reloadKey, setReloadKey] = useState(0); // tăng lên để tải lại bản sau khi admin xử lý 1 ô
  const [subTarget, setSubTarget] = useState(null); // {classId, day, period} — ô CÓ tiết đang mở dialog thay GV/môn (nhấn đúp, chỉ admin)
  const [fillTarget, setFillTarget] = useState(null); // {classId, day, period} — ô TRỐNG của lớp đang mở dialog điền môn+GV

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true);
    setError("");
    Promise.all([
      getTimetableRun(schoolId, runId),
      listClasses(schoolId),
      listSubjects(schoolId),
      getPeriodTimes(schoolId),
      weekId ? getVocationalSchedule(schoolId, weekId) : Promise.resolve({ rows: [] }),
      weekId ? getVanhoaDistribution(schoolId, weekId) : Promise.resolve({ rows: [] }),
      (canEdit || !studentMode) && weekId ? getStaffBusyWeek(schoolId, weekId).catch(() => ({})) : Promise.resolve({}),
      canEdit && weekId ? getPeriodNotes(schoolId, weekId, runId).catch(() => ({})) : Promise.resolve({}),
    ])
      .then(([r, c, s, pt, v, vd, bw, pn]) => {
        setBusyWeek(bw || {});
        setPeriodNotes(pn || {});
        setRun(r);
        setClasses(c.slice().sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true })));
        setSubjects(s);
        setSessions(buildSessions(pt));
        setVoc(v || { rows: [] });
        setVhDist(vd || { rows: [] });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId, canEdit, reloadKey]);

  // Tên GV: grid chỉ ghi teacherId, cần listStaff riêng để tra tên hiện lên.
  useEffect(() => {
    if (!schoolId) return;
    // (24/10/2026) Tên GV tra trên TOÀN BỘ cán bộ (GVCN thuộc Phòng đào tạo... dạy SH vẫn hiện tên).
    Promise.all([listStaff(schoolId, { department: TEACHER_DEPARTMENT }), listStaff(schoolId).catch(() => [])]).then(([t, tAll]) => {
      setTeachers(t.slice().sort((a, b) => a.name.localeCompare(b.name, "vi")));
      setTeacherName(Object.fromEntries([...(tAll || []), ...t].filter((x) => x && x.name).map((x) => [x.id, x.name])));
      setAllStaff(tAll || []);
    });
  }, [schoolId]);

  // (04/11/2026) Phòng học theo tuần (mục "Phân phòng học") — có dữ liệu thì thêm dòng "🚪 Phòng" đầu mỗi buổi.
  const { roomOf, hasAny: showRooms } = useRoomAssignment(schoolId, weekId);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const grid = (run && run.grid) || {};
  const isBusyAt = (teacherId, day, global) => {
    const b = busyWeek[teacherId];
    return !!b && Object.keys(b).some((k) => busyKeyToPairs(k, sessions).some(([d, p]) => d === day && p === global));
  };

  const ngheMap = useMemo(() => {
    const map = {};
    (voc.rows || []).forEach((r) => {
      ngheSlots(r.day).forEach(({ d, s }) => {
        map[`${r.className}|${d}|${s}`] = { subject: r.subject, teacher: r.teacher, room: r.room };
      });
    });
    return map;
  }, [voc]);


  // classId -> "d|p" -> {a, b} — đánh chỉ mục 1 lần cho cả grid.
  const cellsByClass = useMemo(() => {
    const out = {};
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || !val[0] || val[0] === "HN") return;
      const [classId, d, p] = key.split("|");
      const sid = val[0];
      const sn = subjectById[sid] || sid;
      const tName = val[1] ? teacherName[val[1]] || "" : "";
      out[classId] = out[classId] || {};
      out[classId][`${d}|${p}`] = { a: sn, b: tName };
    });
    return out;
  }, [grid, subjectById, teacherName]);

  const classesWithLessons = useMemo(
    () => classes.filter((c) => cellsByClass[c.id]),
    [classes, cellsByClass]
  );
  // Lớp có tiết do mình dạy (cho bộ lọc "Chỉ lớp tôi dạy").
  const myClassIds = useMemo(() => {
    const set = new Set();
    if (myStaffId) Object.entries(grid).forEach(([k, v]) => { if (v && v[1] === myStaffId) set.add(k.split("|")[0]); });
    return set;
  }, [grid, myStaffId]);
  const shownClasses = onlyMine && myStaffId ? classesWithLessons.filter((c) => myClassIds.has(c.id)) : classesWithLessons;

  // Lớp/buổi này có ĐÚNG là buổi nghề theo bảng phân phối nghề-văn hoá
  // (chuẩn) không — TKB nghề (vocationalSchedule) đôi khi có thêm dòng của 1
  // chương trình/lớp nghề khác (vd Mê Linh) rơi vào đúng buổi lẽ ra là văn
  // hoá của lớp đó; nếu không chặn theo bảng chuẩn, dòng thừa này sẽ hiện
  // nhầm thành "HN" (đã gặp thực tế 10A11 Thứ 2 sáng).
  // Danh sách GV cho dialog xử lý ô: GV THPT + cán bộ khối khác đang là GVCN hoặc đã có tiết trong TKB (vd GVCN dạy SH).
  const dialogTeachers = useMemo(() => {
    const have = new Set(teachers.map((t) => t.id));
    const used = new Set([...classes.map((c) => c.homeroomTeacherId), ...Object.values((run && run.grid) || {}).map((v) => (Array.isArray(v) ? v[1] : null))].filter(Boolean));
    return [...teachers, ...allStaff.filter((x) => x && x.name && !have.has(x.id) && used.has(x.id))];
  }, [teachers, allStaff, classes, run]);
  const vhDistByClassName = useMemo(() => Object.fromEntries((vhDist.rows || []).map((r) => [r.className, r])), [vhDist]);
  const isNgheSlot = (className, day, si) => {
    const row = vhDistByClassName[className];
    if (!row || !row.cells || !row.cells[day]) return false;
    return row.cells[day][si === 0 ? "sang" : "chieu"] === "nghe";
  };
  const sessionKind = (className, day, si) => {
    const row = vhDistByClassName[className];
    return row && row.cells && row.cells[day] ? row.cells[day][si === 0 ? "sang" : "chieu"] || null : null;
  };

  const cellFor = (cls, day, si, g) => {
    const direct = (cellsByClass[cls.id] || {})[`${day}|${g}`];
    if (direct) return direct;
    // Nghề LUÔN đúng 4 tiết/buổi (5 tiết thì 1 tiết còn lại văn hoá thật, có
    // thể đầu hay cuối buổi) — chỉ hiện khi Ô LƯỚI đã ghi rõ "HN", KHÔNG suy
    // ra từ ô trống dù cùng buổi nghề: ô trống có thể là 1 tiết văn hoá thật
    // đang thiếu (CP-SAT chưa xếp được), không phải tiết nghề thứ 5 (thực tế
    // 10A12 Thứ 6 sáng: chỉ 4 ô "HN", ô còn lại trống thật).
    const raw = grid[`${cls.id}|${day}|${g}`];
    if (!raw || raw[0] !== "HN") return null;
    if (!isNgheSlot(cls.name, day, si)) return null;
    const n = ngheMap[`${cls.name}|${day}|${si}`] || { subject: "", teacher: "", room: "" }; // (24/10/2026) chưa có lịch nghề chi tiết -> "Học nghề"
    if (n) return { a: (n.subject ? `HN: ${n.subject}` : "Học nghề"), b: [n.teacher, n.room].filter(Boolean).join(" • "), kind: "nghe" };
    return null;
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    classesWithLessons.forEach((cls) => {
      const header = ["Buổi", "Tiết", ...DAYS];
      const aoa = [header];
      sessions.forEach((ses, si) => {
        if (showRooms) aoa.push([ses.label || (si === 0 ? "Sáng" : "Chiều"), "Phòng", ...DAYS.map((_, day) => (roomOf(cls.id, day, si) || {}).label || "")]);
        ses.periods.forEach((p, ti) => {
          const row = [ti === 0 && !showRooms ? (ses.label || (si === 0 ? "Sáng" : "Chiều")) : "", `Tiết ${ti + 1} (${p.time})`];
          DAYS.forEach((_, day) => {
            const cell = cellFor(cls, day, si, p.global);
            row.push(cell ? cell.a + (cell.b ? ` - ${cell.b}` : "") : "");
          });
          aoa.push(row);
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), safeSheetName(cls.name));
    });
    downloadWorkbook(wb, "tkb_toan_truong_gv_hoc_sinh.xlsx");
  };

  // (21/10/2026) In TKB HỌC SINH toàn trường: mỗi lớp 1 trang A4 ngang. Tiết "Nghỉ" và tiết HỌC NGHỀ (HN)
  // để TRỐNG (yêu cầu rõ) — chỉ in tiết văn hoá (môn + GV), Chào cờ/Sinh hoạt vẫn in.
  const printStudentTkb = async () => {
    let schoolName = "";
    try { const list = await listMySchools(); const me = (list || []).find((x) => x.id === schoolId); schoolName = (me && me.name) || ""; } catch (e) { /* bỏ qua */ }
    const esc = (x) => String(x == null ? "" : x).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));
    const wk = (run && run.week) || {};
    const fmt = (d) => (d ? d.split("-").reverse().join("/") : "");
    const weekLabel = wk.startDate ? `Tuần ${fmt(wk.startDate)} – ${fmt(wk.endDate)}` : "";
    const pages = classesWithLessons.map((cls) => {
      const rows = [];
      sessions.forEach((ses, si) => {
        const span = ses.periods.length + (showRooms ? 1 : 0);
        const sesTh = `<th class="b" rowspan="${span}">${esc(ses.label || (si === 0 ? "Sáng" : "Chiều"))}</th>`;
        if (showRooms) rows.push(`<tr class="rm${si > 0 ? " sep" : ""}">${sesTh}<th class="t">Phòng</th>${DAYS.map((_, day) => `<td>${esc((roomOf(cls.id, day, si) || {}).label || "")}</td>`).join("")}</tr>`);
        ses.periods.forEach((p, ti) => {
          const tds = DAYS.map((_, day) => {
            const c = (cellsByClass[cls.id] || {})[`${day}|${p.global}`];
            if (!c || c.a === "Nghỉ") return "<td></td>";
            return `<td><div class="m">${esc(c.a)}</div>${c.b ? `<div class="g">${esc(c.b)}</div>` : ""}</td>`;
          }).join("");
          rows.push(`<tr${ti === 0 && si > 0 && !showRooms ? ' class="sep"' : ""}>${ti === 0 && !showRooms ? sesTh : ""}<th class="t">Tiết ${ti + 1}<div class="tm">${esc(p.time || "")}</div></th>${tds}</tr>`);
        });
      });
      const gvcn = cls.homeroomTeacherId ? teacherName[cls.homeroomTeacherId] || "" : "";
      return `<section class="page"><div class="sch">${esc(schoolName)}</div><h1>THỜI KHOÁ BIỂU LỚP ${esc(cls.name)}</h1>
<div class="sub">${esc(weekLabel)}${gvcn ? ` &nbsp;·&nbsp; GVCN: <b>${esc(gvcn)}</b>` : ""}</div>
<table><thead><tr><th class="b">Buổi</th><th class="t">Tiết</th>${DAYS.map((d) => `<th>${d}</th>`).join("")}</tr></thead><tbody>${rows.join("")}</tbody></table></section>`;
    });
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>TKB học sinh toàn trường</title><style>
@page{size:A4 landscape;margin:10mm}
body{font-family:"Times New Roman",serif;margin:0;color:#000}
.page{page-break-after:always;break-after:page}
.page:last-child{page-break-after:auto;break-after:auto}
.sch{font-size:13pt;font-weight:bold;text-transform:uppercase}
h1{text-align:center;font-size:20pt;margin:4mm 0 1mm}
.sub{text-align:center;font-size:12pt;margin-bottom:4mm}
table{width:100%;border-collapse:collapse;table-layout:fixed}
th,td{border:1px solid #000;text-align:center;vertical-align:middle;padding:1.2mm 1mm;font-size:11pt;height:12mm}
thead th{background:#eee;font-size:12pt;height:8mm}
th.b{width:14mm}th.t{width:20mm;font-weight:normal}
.tm{font-size:8pt;color:#333}
.m{font-weight:bold}.g{font-size:9pt;font-style:italic}
tr.sep th,tr.sep td{border-top:2.5px solid #000}
tr.rm td,tr.rm th.t{height:6mm;font-weight:bold;font-size:10pt;background:#f3f3f3}
</style></head><body>${pages.join("")}<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { window.alert("Trình duyệt chặn cửa sổ in — hãy cho phép pop-up rồi bấm lại."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  if (!runId) return <p style={{ color: "#6b7280" }}>Chưa chọn bản xếp TKB.</p>;
  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (classesWithLessons.length === 0) return <p style={{ color: "#6b7280" }}>Chưa có lớp nào có tiết trong bản này.</p>;

  return (
    <div>
      {studentMode ? (
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        TKB học sinh của {classesWithLessons.length} lớp — tiết <b>Nghỉ</b> và tiết <b>học nghề</b> để trống. Bấm <b>🖨 In</b> để in toàn trường, mỗi lớp 1 trang A4 ngang.
      </p>
      ) : (
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        TKB của toàn bộ {classesWithLessons.length} lớp có tiết trong bản này (kèm tên GV, kể cả buổi học nghề), xếp
        liên tiếp — lướt xuống để soát từng lớp thay vì chọn qua combobox.
      </p>
      )}
      <button
        type="button"
        onClick={exportExcel}
        style={{ marginBottom: 12, padding: "8px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
      >
        ⬇ Xuất Excel (mỗi lớp 1 sheet)
      </button>
      <button
        type="button"
        onClick={printStudentTkb}
        style={{ marginBottom: 12, marginLeft: 8, padding: "8px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
      >
        🖨 In TKB học sinh (mỗi lớp 1 trang)
      </button>
      {canEdit && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: swapMode ? "#ecfdf5" : "#f9fafb", border: `1px solid ${swapMode ? "#a7f3d0" : "#e5e7eb"}`, borderRadius: 8, fontSize: 13 }}>
          <label style={{ cursor: "pointer", fontWeight: 600, color: "#065f46" }}>
            <input type="checkbox" checked={swapMode} onChange={(e) => setSwapMode(e.target.checked)} /> ⇄ Chế độ đổi tiết
          </label>
          <span style={{ color: "#6b7280" }}> — bật lên rồi bấm vào tiết bất kỳ để đổi với 1 tiết khác trong cùng lớp (tắt đi để nhấn đúp thay GV / điền ô như cũ).</span>
        </div>
      )}
      {swapEnabled && !canEdit && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, fontSize: 13, color: "#065f46", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <span>⇄ <b>Đổi tiết:</b> bấm vào tiết của bạn (ô <b>viền xanh</b>) để đổi với 1 tiết khác trong cùng lớp.</span>
          <label style={{ cursor: "pointer" }}><input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Chỉ hiện lớp tôi dạy ({myClassIds.size})</label>
        </div>
      )}
      <div style={{ marginBottom: 16, padding: "8px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
        {shownClasses.map((c) => (
          <a key={c.id} href={`#lop-${c.id}`} style={{ fontSize: 12, color: "#0f766e", textDecoration: "none" }}>{c.name}</a>
        ))}
      </div>
      {shownClasses.map((cls) => {
        const cellsOfClass = Object.values(cellsByClass[cls.id] || {});
        const vanhoaCount = cellsOfClass.filter((c) => !EXCLUDED_FROM_VANHOA_TOTAL.has(c.a)).length;
        const hdtnCount = cellsOfClass.filter((c) => HDTN_CATEGORY.has(c.a)).length;
        const hdtnOk = hdtnCount === 3;
        return (
        <div key={cls.id} id={`lop-${cls.id}`} style={{ marginBottom: 28, scrollMarginTop: 10 }}>
          <h4 style={{ margin: "0 0 8px" }}>
            🎒 TKB {cls.name}{cls.classType ? ` • ${cls.classType}` : ""}{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              — Tổng {vanhoaCount} tiết văn hoá + {" "}
              <span style={{ color: hdtnOk ? "#6b7280" : "#dc2626", fontWeight: hdtnOk ? 400 : 700 }}>
                {hdtnCount} tiết HĐTN
              </span>
              {" "}= {vanhoaCount + hdtnCount}/tuần
            </span>
          </h4>
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thFix(0), minWidth: 60, zIndex: 6 }}>Buổi</th>
                  <th style={{ ...thFix(60), minWidth: 56, zIndex: 6 }}>Tiết</th>
                  {DAYS.map((d) => (<th key={d} style={{ ...th, minWidth: 120 }}>{d}</th>))}
                </tr>
              </thead>
              <tbody>
                {sessions.map((ses, si) => (
                  <React.Fragment key={si}>
                  {showRooms && (
                    <RoomRow classId={cls.id} si={si} rowSpan={ses.periods.length + 1} sesLabel={ses.label} roomOf={roomOf} cellStyle={td}
                      labelStyle={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }} />
                  )}
                  {ses.periods.map((p, ti) => (
                    <tr key={`${si}-${ti}`} data-pi={periodShadeIndex(p.global)}>
                      {ti === 0 && !showRooms && (
                        <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }}>{ses.label}</td>
                      )}
                      <td style={{ ...td, position: "sticky", left: 60, zIndex: 5, background: "#fff", whiteSpace: "nowrap" }}>Tiết {ti + 1}<div style={{ color: "#9ca3af", fontSize: 10 }}>{p.time}</div></td>
                      {DAYS.map((d, day) => {
                        const cell0 = cellFor(cls, day, si, p.global);
                        const cell = studentMode && cell0 && (cell0.kind === "nghe" || cell0.a === "Nghỉ") ? null : cell0;
                        const raw = grid[`${cls.id}|${day}|${p.global}`];
                        // Admin nhấn đúp: ô CÓ tiết (môn thật) -> dialog thay GV/môn/để trống/trông lớp; ô TRỐNG (không phải buổi nghề/HN) -> dialog điền môn + GV.
                        const hasLesson = !!(cell && cell.kind !== "nghe" && raw && subjectById[raw[0]]);
                        // (18/10/2026) Buổi nghề: chỉ khoá ô "HN" (tiết nghề thật); tiết 5/10 còn trống hoặc "Nghỉ" vẫn cho điền văn hoá.
                        // (23/10/2026) Ô "HN" (tiết học nghề) cũng cho nhấn đúp để điền/thay bằng tiết văn hoá.
                        const isEmpty = !cell || !!(raw && raw[0] === "HN");
                        const onDbl = !canEdit ? undefined : hasLesson ? () => setSubTarget({ classId: cls.id, day, period: p.global }) : isEmpty ? () => setFillTarget({ classId: cls.id, day, period: p.global }) : undefined;
                        // Tiết của chính mình -> đổi tiết. Admin (đã có nhấn đúp) dùng nút ⇄ nhỏ để không lẫn với nhấn đúp; GV bấm cả ô.
                        const isMine = swapEnabled && hasLesson && raw[1] === myStaffId;
                        const adminSwap = canEdit && swapMode && hasLesson;
                        const openSwap = isMine || adminSwap ? () => setSwapTarget({ classId: cls.id, day, period: p.global }) : undefined;
                        return (
                          <td key={d} onDoubleClick={swapMode ? undefined : onDbl} onClick={!canEdit || swapMode ? openSwap : undefined}
                            title={isMine ? (canEdit ? "Tiết của bạn — bấm ⇄ để đổi tiết; nhấn đúp để đổi GV / môn" : "Tiết của bạn — bấm để đổi tiết") : onDbl ? (hasLesson ? "Nhấn đúp để đổi GV / môn / để trống / trông lớp" : "Ô trống — nhấn đúp để điền môn và giáo viên") : undefined}
                            style={{ ...td, minWidth: 110, cursor: onDbl || isMine ? "pointer" : undefined, background: isMine ? "#ecfdf5" : cell ? (cell.kind === "nghe" ? "#fef3c7" : "#eff6ff") : "#fff", boxShadow: isMine ? "inset 0 0 0 2px #10b981" : undefined, position: isMine && canEdit ? "relative" : undefined }}>
                            {cell ? (<><div>{cell.a}</div>{cell.b && <div style={{ color: "#6b7280", fontSize: 11 }}>{cell.b}</div>}</>) : ""}
                            {isMine && canEdit && !swapMode && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); openSwap(); }} onDoubleClick={(e) => e.stopPropagation()}
                                title="Đổi tiết" style={{ position: "absolute", top: 1, right: 1, fontSize: 10, padding: "0 4px", border: "1px solid #10b981", background: "#fff", color: "#047857", borderRadius: 4, cursor: "pointer" }}>⇄</button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })}
      {subTarget && (
        <HappySchoolCellSubstituteDialog
          schoolId={schoolId} runId={runId} weekId={weekId} target={subTarget} grid={grid} classes={classes} subjects={subjects}
          teachers={dialogTeachers} isBusyAt={isBusyAt} periodNotes={periodNotes}
          onClose={() => setSubTarget(null)}
          onSaved={() => { setSubTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
      {swapTarget && (
        <HappySchoolCellSwapDialog
          schoolId={schoolId} runId={runId} target={swapTarget} grid={grid} classes={classes} subjects={subjects}
          teacherName={teacherName} sessions={sessions} isBusyAt={isBusyAt} sessionKind={sessionKind} published={!!(run && run.published && run.publishMode !== "demo")}
          onClose={() => setSwapTarget(null)}
          onSaved={() => { setSwapTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
      {fillTarget && (
        <HappySchoolCellFillDialog
          schoolId={schoolId} runId={runId} weekId={weekId} target={fillTarget} grid={grid} classes={classes} subjects={subjects}
          teachers={dialogTeachers} isBusyAt={isBusyAt}
          onClose={() => setFillTarget(null)}
          onSaved={() => { setFillTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center", position: "sticky" };
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center", verticalAlign: "top" };

export default HappySchoolAllClassesView;
